import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { Spinner } from '../../components/ui.jsx'

export function Settings({ me }) {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [confirm, setConfirm] = useState('')
  const [keepData, setKeepData] = useState(false)

  const proj = useQuery({ queryKey: ['project', slug], queryFn: () => api.project(slug) })
  const prov = useQuery({ queryKey: ['provisioning', slug], queryFn: () => api.provisioning(slug) })
  const qc = useQueryClient()
  const borrar = useMutation({
    mutationFn: () => api.deleteProject(slug, confirm, keepData),
    onSuccess: () => {
      // Sin esto, la portada pintaba la lista CACHEADA y el proyecto seguía ahí como si nada
      // se hubiera borrado — el peor mensaje posible después de escribir el nombre para
      // confirmar. Se tiran las consultas del proyecto y se refresca la lista antes de navegar.
      qc.removeQueries({ queryKey: ['project', slug] })
      qc.removeQueries({ queryKey: ['provisioning', slug] })
      qc.removeQueries({ queryKey: ['overview', slug] })
      qc.removeQueries({ queryKey: ['functions', slug] })
      qc.removeQueries({ queryKey: ['jobs', slug] })
      qc.invalidateQueries({ queryKey: ['projects'] })
      navigate('/', { replace: true })
    },
  })

  if (proj.isPending) return <Spinner />

  return (
    <>
      <h1 className="h1">Ajustes</h1>
      <p className="lede">Configuración e infraestructura de <span className="mono">{slug}</span>.</p>

      <div className="card">
        <div className="card-head"><span className="h2" style={{ fontSize: 14 }}>Infraestructura</span></div>
        <div className="table-scroll">
          <table className="t">
            <tbody>
              <tr><td style={{ color: 'var(--fg-2)', width: 210 }}>Estado</td><td className="mono">{prov.data?.state ?? proj.data.state}</td></tr>
              <tr><td style={{ color: 'var(--fg-2)' }}>Repositorio</td><td className="mono">{proj.data.gitlabPath ?? '—'}</td></tr>
              <tr><td style={{ color: 'var(--fg-2)' }}>Dominio</td><td className="mono">{proj.data.domain ?? '—'}</td></tr>
              <tr><td style={{ color: 'var(--fg-2)' }}>Base de datos</td><td className="mono">{proj.data.dbName ?? 'ninguna'}</td></tr>
              <tr>
                <td style={{ color: 'var(--fg-2)' }}>Puerto de la app</td>
                <td><PuertoApp slug={slug} actual={proj.data.appPort ?? 3000} esAdmin={proj.data.role === 'admin'} /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {prov.data?.steps?.some((s) => s.state !== 'pending') && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-head"><span className="h2" style={{ fontSize: 14 }}>Aprovisionamiento</span></div>
          <div style={{ padding: '6px 0' }}>
            {prov.data.steps.map((s) => (
              <div key={s.id} className="prov-step">
                <span className="prov-icon">
                  {s.state === 'done' ? <span style={{ color: 'var(--success)' }}>✓</span>
                    : s.state === 'failed' ? <span style={{ color: 'var(--danger)' }}>✕</span>
                    : s.state === 'skipped' ? <span style={{ color: 'var(--fg-3)' }}>–</span>
                    : <span style={{ color: 'var(--fg-3)' }}>○</span>}
                </span>
                <span style={{ color: s.state === 'pending' ? 'var(--fg-3)' : 'var(--fg)' }}>{s.titulo}</span>
                {s.error && <span className="prov-error">{s.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {proj.data.protegido && me?.platformAdmin && (
        <div className="card card-pad" style={{ marginTop: 26 }}>
          <strong>Proyecto protegido de la plataforma</strong>
          <p style={{ margin: '6px 0 0', color: 'var(--fg-2)', fontSize: 13 }}>
            No puede darse de baja desde la consola — este proyecto ES la consola (o parte de la
            plataforma). Si de verdad hay que retirarlo: quitar el slug de{' '}
            <span className="mono">CONSOLE_PROTECTED_PROJECTS</span> y redesplegar, y entonces
            aparecerá la zona de peligro. Una decisión de despliegue, no un clic.
          </p>
        </div>
      )}

      {!proj.data.protegido && me?.platformAdmin && (
        <div className="card danger-zone" style={{ marginTop: 26 }}>
          <div className="card-head"><span className="h2" style={{ fontSize: 14, color: 'var(--danger)' }}>Zona de peligro</span></div>
          <div className="card-pad">
            <p style={{ marginTop: 0, color: 'var(--fg-2)', fontSize: 13 }}>
              Eliminar el proyecto libera el dominio y su certificado, la aplicación en Dokploy,
              el deploy token, el webhook y el folder de Windmill. <strong>La base de datos se
              borra y no hay copia</strong>: son datos de desarrollo y así está declarado en el plan.
              El repositorio de GitLab no se toca.
            </p>

            <label style={{ display: 'flex', gap: 9, alignItems: 'center', fontSize: 13, margin: '14px 0' }}>
              <input type="checkbox" checked={keepData} onChange={(e) => setKeepData(e.target.checked)} />
              Conservar la base de datos
            </label>

            <label className="field-label" htmlFor="confirm">
              Escribe <span className="mono">{slug}</span> para confirmar
            </label>
            <input id="confirm" className="input wide" value={confirm}
                   onChange={(e) => setConfirm(e.target.value)} placeholder={slug} />

            <div style={{ marginTop: 14 }}>
              <button className="btn danger" disabled={confirm !== slug || borrar.isPending}
                      onClick={() => borrar.mutate()}>
                {borrar.isPending ? 'Eliminando…' : 'Eliminar proyecto'}
              </button>
            </div>

            {borrar.isError && (
              <div className="alert error" style={{ marginTop: 14 }}>
                {borrar.error.body?.detail ?? borrar.error.message}
                {borrar.error.body?.pendiente && (
                  <div style={{ marginTop: 8, fontSize: 12 }}>
                    Sin liberar: {borrar.error.body.pendiente.map((f) => f.nombre).join(', ')}.
                    El reconciliador lo reintentará en unos minutos.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}


/**
 * Puerto configurable (roadmap §12). Cambiarlo reescribe PORT, recrea el dominio de Dokploy
 * apuntando al puerto nuevo y REDESPLIEGA: sin ese último paso el cambio sería mentira.
 */
function PuertoApp({ slug, actual, esAdmin }) {
  const qc = useQueryClient()
  const [valor, setValor] = useState(null) // null = sin editar
  const cambiar = useMutation({
    mutationFn: (puerto) => api.setPort(slug, puerto),
    onSuccess: () => { setValor(null); qc.invalidateQueries({ queryKey: ['project', slug] }) },
  })
  if (!esAdmin) return <span className="mono">{actual}</span>
  if (valor == null) {
    return (
      <span className="mono">
        {actual}{' '}
        <button className="btn sm secondary" style={{ marginLeft: 8 }} onClick={() => setValor(String(actual))}>
          Cambiar
        </button>
      </span>
    )
  }
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      <input className="input" type="number" min="1" max="65535" value={valor}
             style={{ width: 110 }} onChange={(e) => setValor(e.target.value)} />
      <button className="btn sm primary" disabled={cambiar.isPending}
              onClick={() => cambiar.mutate(Number(valor))}>
        {cambiar.isPending ? 'Aplicando…' : 'Aplicar y redesplegar'}
      </button>
      <button className="btn sm secondary" disabled={cambiar.isPending} onClick={() => setValor(null)}>
        Cancelar
      </button>
      {cambiar.isError && (
        <span style={{ color: 'var(--danger)', fontSize: 12 }}>
          {cambiar.error.body?.detail ?? cambiar.error.message}
        </span>
      )}
    </span>
  )
}